// JobSpecAuthoringPanel — Layer 3b
// Route-less full-page panel for editing the photo + checklist
// requirements of a JobSpec. Mounted via portal-less overlay (fixed
// positioning over the current view) so it has the room of a full page
// without introducing a new route.
//
// Scope (locked):
//   • Edits requiredPhotos toggles + checklistItems (label + required flag)
//   • No phase name / order / count changes
//   • No BOM / SKU edits
//   • No critical flag (deferred)
//   • Publish creates a new spec version (overlay) — bound jobs untouched
//   • Unsaved-changes guard before close
//   • Typed CONFIRM on publish
//
// Role gate: SPEC_AUTHORING_EDITORS (see jobSpecAuthoring.ts).

import { useEffect, useMemo, useState } from "react";
import {
  X,
  Camera,
  CheckSquare,
  Plus,
  Trash2,
  Save,
  Send,
  History,
  AlertTriangle,
  Lock,
  Check,
  Sparkles,
} from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useInstallerExecution } from "@/data/InstallerExecutionContext";
import {
  getSpecVersionTelemetry,
  getSpecRecommendations,
} from "@/data/jobSpecTelemetry";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ALL_REQUIRED_PHOTO_TYPES,
  getPhotoLabel,
  type JobSpec,
  type RequiredPhotoType,
} from "@/data/jobSpec";
import {
  toAuthoringSpec,
  summarizeChanges,
  specsEqual,
  type AuthoringChecklistItem,
  type AuthoringPhase,
  type AuthoringSpec,
} from "@/data/jobSpecAuthoring";
import { useJobSpecAuthoring } from "@/data/JobSpecAuthoringContext";
import { useRole } from "@/data/RoleContext";

// ── Advisory recommendations strip (Layer 3c) ──
// Reads telemetry for the latest published version of this spec and surfaces
// proof-gap-derived suggestions. Advisory only — never auto-mutates the draft.
function SpecRecommendationsBanner({ baseSpec }: { baseSpec: JobSpec }) {
  const { jobs, specExecutions } = useAppData();
  const { proofGapEvents } = useInstallerExecution();

  const telemetry = getSpecVersionTelemetry({
    spec: baseSpec,
    jobs,
    specExecutions,
    proofGapEvents,
  });
  const recs = getSpecRecommendations(telemetry);

  if (telemetry.empty.noBoundJobs || telemetry.empty.noProofGapEvents || recs.length === 0) {
    return null;
  }

  return (
    <div className="border-b bg-primary/5 px-6 py-3">
      <div className="flex items-start gap-3">
        <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Recommendations from v{telemetry.versionNumber} field signal
            </p>
            <Badge variant="outline" className="text-[9px] uppercase tracking-wider">
              advisory
            </Badge>
          </div>
          <ul className="mt-1.5 space-y-1">
            {recs.slice(0, 3).map((r) => (
              <li key={r.id} className="text-xs text-foreground/80">
                • {r.message}
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            These are suggestions, not changes. Review and decide manually.
          </p>
        </div>
      </div>
    </div>
  );
}

interface Props {
  /** Source spec (registry canonical). Drives initial draft if none in flight. */
  baseSpec: JobSpec;
  open: boolean;
  onClose: () => void;
}

export function JobSpecAuthoringPanel({ baseSpec, open, onClose }: Props) {
  const { role } = useRole();
  const {
    getDraft,
    getChangeLog,
    saveDraft,
    discardDraft,
    publishDraft,
  } = useJobSpecAuthoring();

  const [working, setWorking] = useState<AuthoringSpec | null>(null);
  const [activePhaseIdx, setActivePhaseIdx] = useState(0);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");

  // Initialize working copy when panel opens.
  useEffect(() => {
    if (!open) return;
    const existingDraft = getDraft(baseSpec.specId);
    setWorking(existingDraft ?? toAuthoringSpec(baseSpec));
    setActivePhaseIdx(0);
    setConfirmText("");
    setReason("");
  }, [open, baseSpec, getDraft]);

  const baseline = useMemo(() => toAuthoringSpec(baseSpec), [baseSpec]);
  const dirty = working ? !specsEqual(baseline, working) : false;
  const changeLog = getChangeLog(baseSpec.specId);

  // ── Mutators ────────────────────────────────────────────────────────────
  const updatePhase = (idx: number, patch: Partial<AuthoringPhase>) => {
    setWorking((prev) => {
      if (!prev) return prev;
      const phases = prev.phases.slice();
      phases[idx] = { ...phases[idx], ...patch };
      return { ...prev, phases };
    });
  };

  const togglePhoto = (phaseIdx: number, photoType: RequiredPhotoType) => {
    setWorking((prev) => {
      if (!prev) return prev;
      const phases = prev.phases.slice();
      const phase = phases[phaseIdx];
      const has = phase.requiredPhotos.includes(photoType);
      phases[phaseIdx] = {
        ...phase,
        requiredPhotos: has
          ? phase.requiredPhotos.filter((p) => p !== photoType)
          : [...phase.requiredPhotos, photoType],
      };
      return { ...prev, phases };
    });
  };

  const addChecklistItem = (phaseIdx: number) => {
    setWorking((prev) => {
      if (!prev) return prev;
      const phases = prev.phases.slice();
      const phase = phases[phaseIdx];
      const newItem: AuthoringChecklistItem = {
        id: `${phase.name}-new-${Date.now()}`,
        label: "",
        required: true,
      };
      phases[phaseIdx] = {
        ...phase,
        checklistItems: [...phase.checklistItems, newItem],
      };
      return { ...prev, phases };
    });
  };

  const updateChecklistItem = (
    phaseIdx: number,
    itemId: string,
    patch: Partial<AuthoringChecklistItem>,
  ) => {
    setWorking((prev) => {
      if (!prev) return prev;
      const phases = prev.phases.slice();
      const phase = phases[phaseIdx];
      phases[phaseIdx] = {
        ...phase,
        checklistItems: phase.checklistItems.map((c) =>
          c.id === itemId ? { ...c, ...patch } : c,
        ),
      };
      return { ...prev, phases };
    });
  };

  const removeChecklistItem = (phaseIdx: number, itemId: string) => {
    setWorking((prev) => {
      if (!prev) return prev;
      const phases = prev.phases.slice();
      const phase = phases[phaseIdx];
      phases[phaseIdx] = {
        ...phase,
        checklistItems: phase.checklistItems.filter((c) => c.id !== itemId),
      };
      return { ...prev, phases };
    });
  };

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleSaveDraft = () => {
    if (!working) return;
    saveDraft(baseSpec.specId, working, { changedBy: role });
    toast.success("Draft saved", { description: summarizeChanges(baseline, working) });
  };

  const handleClose = () => {
    if (dirty) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardAndClose = () => {
    discardDraft(baseSpec.specId, { changedBy: role });
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleOpenPublish = () => {
    if (!working) return;
    if (!dirty && !getDraft(baseSpec.specId)) {
      toast.info("No changes to publish");
      return;
    }
    // Validation: every checklist item must have a non-empty label.
    const blankItems = working.phases.flatMap((p) =>
      p.checklistItems.filter((c) => !c.label.trim()).map(() => p.label),
    );
    if (blankItems.length > 0) {
      toast.error("Resolve blank checklist items before publishing", {
        description: `Empty labels in: ${[...new Set(blankItems)].join(", ")}`,
      });
      return;
    }
    setConfirmText("");
    setReason("");
    setPublishOpen(true);
  };

  const handlePublish = () => {
    if (!working) return;
    if (confirmText !== "CONFIRM") return;
    if (!reason.trim()) {
      toast.error("A reason is required to publish");
      return;
    }
    // Always save draft first so publishDraft has a draft to consume.
    saveDraft(baseSpec.specId, working, { changedBy: role });
    try {
      const { newVersionNumber } = publishDraft(baseSpec.specId, {
        changedBy: role,
        reason: reason.trim(),
      });
      toast.success(`Published v${newVersionNumber}`, {
        description: "New jobs will bind to this version. Existing bound jobs are unaffected.",
      });
      setPublishOpen(false);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Publish failed");
    }
  };

  if (!open || !working) return null;

  const activePhase = working.phases[activePhaseIdx];

  return (
    <>
      {/* Full-page route-less panel */}
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Header */}
        <header className="border-b px-6 py-4 flex items-center justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              JobSpec Editor
            </p>
            <h1 className="text-lg font-semibold truncate">{working.label}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              <code className="font-mono">{working.specId}</code> • base v{baseSpec.versionNumber}
              {dirty && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3" /> Unsaved changes
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={!dirty}>
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save Draft
            </Button>
            <Button size="sm" onClick={handleOpenPublish}>
              <Send className="h-3.5 w-3.5 mr-1.5" /> Publish…
            </Button>
            <Button variant="ghost" size="icon" onClick={handleClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <SpecRecommendationsBanner baseSpec={baseSpec} />

        {/* Body — phase rail + editor + change log */}
        <div className="flex-1 min-h-0 grid grid-cols-[200px_1fr_280px] gap-0">
          {/* Phase rail (left) */}
          <nav className="border-r bg-muted/20 overflow-y-auto p-3 space-y-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground px-2 mb-2">
              Phases
            </p>
            {working.phases.map((p, i) => (
              <button
                key={p.name}
                onClick={() => setActivePhaseIdx(i)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  i === activePhaseIdx
                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                    : "hover:bg-muted"
                }`}
              >
                <div className="font-medium">{p.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {p.requiredPhotos.length} photos • {p.checklistItems.length} checks
                </div>
              </button>
            ))}
            <div className="px-2 pt-3 text-[10px] text-muted-foreground border-t mt-3">
              <Lock className="h-3 w-3 inline mr-1" />
              Phase names & order are fixed.
            </div>
          </nav>

          {/* Phase editor (center) */}
          <main className="overflow-y-auto p-6 space-y-6">
            {/* Required Photos */}
            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                      <Camera className="h-4 w-4 text-primary" /> Required Photos
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Toggle which photo types this phase requires. Proof gating is
                      controlled by Proof Enforcement Policy in Settings.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {activePhase.requiredPhotos.length} selected
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_REQUIRED_PHOTO_TYPES.map((pt) => {
                    const checked = activePhase.requiredPhotos.includes(pt);
                    return (
                      <label
                        key={pt}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md border text-xs cursor-pointer transition-colors ${
                          checked
                            ? "bg-primary/10 border-primary/40"
                            : "hover:bg-muted/40"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePhoto(activePhaseIdx, pt)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="flex-1">{getPhotoLabel(pt)}</span>
                        {checked && <Check className="h-3 w-3 text-primary" />}
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Checklist */}
            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 text-primary" /> Checklist Items
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Items marked Required gate phase submit (under active proof
                      policy). Non-required items are informational only.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => addChecklistItem(activePhaseIdx)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {activePhase.checklistItems.length === 0 && (
                    <p className="text-xs text-muted-foreground italic px-1">
                      No checklist items. Click Add Item to create one.
                    </p>
                  )}
                  {activePhase.checklistItems.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/10"
                    >
                      <Input
                        value={c.label}
                        onChange={(e) =>
                          updateChecklistItem(activePhaseIdx, c.id, { label: e.target.value })
                        }
                        placeholder="Checklist item label…"
                        className="flex-1 h-8 text-xs"
                      />
                      <div className="flex items-center gap-2 shrink-0">
                        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Required
                        </Label>
                        <Switch
                          checked={c.required}
                          onCheckedChange={(v) =>
                            updateChecklistItem(activePhaseIdx, c.id, { required: v })
                          }
                        />
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeChecklistItem(activePhaseIdx, c.id)}
                        aria-label="Remove"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </main>

          {/* Change log (right) */}
          <aside className="border-l bg-muted/10 overflow-y-auto p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-3 flex items-center gap-1.5">
              <History className="h-3 w-3" /> Change Log
            </p>
            {changeLog.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                No changes yet. The most recent 50 are kept.
              </p>
            )}
            <ul className="space-y-2">
              {changeLog.map((e) => (
                <li key={e.id} className="text-xs border rounded-md p-2 bg-background">
                  <div className="flex items-center justify-between gap-2">
                    <Badge
                      variant={e.action === "published" ? "default" : "outline"}
                      className="text-[9px] uppercase"
                    >
                      {e.action.replace("_", " ")}
                      {e.publishedVersion ? ` v${e.publishedVersion}` : ""}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(e.changedAt).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="mt-1 text-foreground/90">{e.summary}</p>
                  {e.reason && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground italic">
                      "{e.reason}"
                    </p>
                  )}
                  <p className="mt-0.5 text-[10px] text-muted-foreground">by {e.changedBy}</p>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>

      {/* Unsaved changes guard */}
      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard unsaved changes?</DialogTitle>
            <DialogDescription>
              You have edits that haven't been saved. Closing now will discard them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmCloseOpen(false)}>
              Keep editing
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                handleSaveDraft();
                setConfirmCloseOpen(false);
                onClose();
              }}
            >
              Save draft & close
            </Button>
            <Button variant="destructive" onClick={handleDiscardAndClose}>
              Discard & close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish confirmation */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Publish new spec version?</DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                A new version of <code>{baseSpec.specId}</code> will be created.
                New jobs will bind to it. Already-bound jobs are unaffected.
              </span>
              <span className="block text-xs">
                Changes: {working ? summarizeChanges(baseline, working) : "—"}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Reason for change</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Added required pressure test photo per QA review"
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Type <code className="bg-muted px-1 py-0.5 rounded">CONFIRM</code> to publish
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
                className="font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPublishOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={confirmText !== "CONFIRM" || !reason.trim()}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default JobSpecAuthoringPanel;
